import { Injectable, inject } from '@angular/core';

import { ApolloError } from '@apollo/client/errors';
import { Apollo } from 'apollo-angular';
import { QueryOptionsAlone } from 'apollo-angular/types';
import { EMPTY, Observable, catchError, concat, defer, filter, map, of, switchMap, take, tap } from 'rxjs';

import {
  Banner,
  FetchPolicy,
  MiniTeaserBanner,
  ProductBannerFilterInput,
  QueryGridPageProductBannerArgs,
  QueryMegaMenuProductBannerArgs
} from '@hdms/shared/common-types';
import { GQL_RESPONSE_CACHE_TTL_HEADER, PlatformService, StorageService } from '@hdms/shared/util-core';

import { BannerHelperService } from './banner-helper.service';
import { CustomerJourneyService } from './customer-journey.service';
import { GridPageProductBannerGqlService } from './grid-page-product-banner-gql.service';
import { MarketingBannerGqlService } from './marketing-banner-gql.service';
import { MegaMenuProductBannerGqlService } from './mega-menu-product-banner-gql.service';
import { MiniTeaserBannersGqlService } from './mini-teaser-banners-gql.service';
import { TemporarilyBanBannerGqlService } from './temporarily-ban-banner-gql.service';

export const PERMANENTLY_BANNED_BANNER_STORAGE_KEY = 'bannedBannersPermanently';
const PERMANENTLY_BANNED_BANNER_LIMIT = 40;

export interface ProductBanner extends Banner {
  loaded?: boolean;
}

// 4h cache TTL for default product banners GQL response
const BANNER_CACHE_TTL = 14400;

// query options for configuring Redis response caching on server side
const BANNER_CACHE_OPTIONS: QueryOptionsAlone = {
  context: { headers: { [GQL_RESPONSE_CACHE_TTL_HEADER]: String(BANNER_CACHE_TTL) } }
};

export enum BannerErrorCode {
  INVALID_JOURNEY_ID = 'INVALID_JOURNEY_ID'
}

@Injectable({
  providedIn: 'root'
})
export class BannerService {
  public readonly marketingBanner$: Observable<Banner>;
  public readonly megaMenuProductBanner$: Observable<ProductBanner>;
  public readonly gridPageProductBanner$: Observable<ProductBanner>;
  public readonly categoryMiniTeaserBanners$: Observable<MiniTeaserBanner[]>;
  public readonly megaMenuMiniTeaserBanners$: Observable<MiniTeaserBanner[]>;

  private readonly storageService = inject(StorageService);
  private readonly journeyService = inject(CustomerJourneyService);
  private readonly platform = inject(PlatformService);
  private readonly helper = inject(BannerHelperService);
  private readonly apolloCache = inject(Apollo).client.cache;

  private readonly temporarilyBanBannerGql = inject(TemporarilyBanBannerGqlService);
  private readonly megaMenuProductBannerGql = inject(MegaMenuProductBannerGqlService);
  private readonly gridPageProductBannerGql = inject(GridPageProductBannerGqlService);
  private readonly marketingBannerGql = inject(MarketingBannerGqlService);
  private readonly miniTeaserBannersGql = inject(MiniTeaserBannersGqlService);

  private bannedMarketingBanners = this.getBannedMarketingBanners();

  constructor() {
    this.marketingBanner$ = this.getMarketingBannerData$();
    this.megaMenuProductBanner$ = this.getMegaMenuProductBannerData$();
    this.gridPageProductBanner$ = this.getGridPageProductBannerData$();
    this.categoryMiniTeaserBanners$ = this.getMiniTeaserBanners$(true);
    this.megaMenuMiniTeaserBanners$ = this.getMiniTeaserBanners$();

    this.preloadDefaultBannerData();
    this.purgeCachedFinalProductBanner();
  }

  /**
   * Marketing banners can be requested if
   * - a guid is present
   * - not on homepage
   * - current page has a hilti referrer
   * @private
   */
  private get isMarketingBannerEnabled(): boolean {
    return this.journeyService.guid && this.helper.finalUrl !== '/' && this.helper.hasHiltiReferrer;
  }

  private get bannerFilterInput(): ProductBannerFilterInput {
    return {
      uri: this.helper.finalUrl,
      journeyId: this.journeyService.guid,
      cartId: this.helper.cartId,
      pageType: this.helper.pageType,
      productId: this.helper.productId
    };
  }

  private get productBannerInputArgs(): QueryMegaMenuProductBannerArgs | QueryGridPageProductBannerArgs {
    return {
      filter: this.bannerFilterInput,
      localization: this.helper.localization
    };
  }

  temporarilyBanMarketingBanner$(banner: Banner, trackingEnabled = true): Observable<unknown> {
    trackingEnabled && this.helper.trackBannerClose(banner, 'Close');
    return this.journeyService.guid
      ? this.temporarilyBanBannerGql
          .mutate({
            input: {
              bannerId: banner.id,
              journeyId: this.journeyService.guid
            },
            localization: this.helper.localization
          })
          .pipe(
            catchError(err => {
              this.handleInvalidJourneyIdError(err);
              return of(null);
            })
          )
      : of(null);
  }

  permanentlyBanMarketingBanner(banner: Banner, trackingEnabled = true): void {
    trackingEnabled && this.helper.trackBannerClose(banner, 'OptOut');
    this.updateBannedMarketingBanners(banner.id);
  }

  private getBannedMarketingBanners(): string[] {
    return this.storageService.getItem<string[]>(PERMANENTLY_BANNED_BANNER_STORAGE_KEY);
  }

  private updateBannedMarketingBanners(bannerId: string): void {
    this.bannedMarketingBanners = [...new Set(this.getBannedMarketingBanners()).add(bannerId)];

    if (this.bannedMarketingBanners.length > PERMANENTLY_BANNED_BANNER_LIMIT) {
      this.bannedMarketingBanners.shift();
    }

    this.storageService.setItem(PERMANENTLY_BANNED_BANNER_STORAGE_KEY, this.bannedMarketingBanners);
  }

  private getMegaMenuProductBannerData$(): Observable<ProductBanner> {
    return this.getProductBannerData$(this.getDefaultMegaMenuProductBanner$(), this.getFinalMegaMenuProductBanner$());
  }

  private getGridPageProductBannerData$(): Observable<ProductBanner> {
    return this.getFinalProductBanner(this.getFinalGridPageProductBanner$(), null);
  }

  /**
   * First, load default banner, ideally already retrieved from Apollo Cache,
   * then wait until customer journey update is submitted, before loading
   * final banner. Final banner will be loaded client side only.
   * In case no final product banner received, return default banner.
   * If no default banner received, return empty observable.
   *
   * @param defaultBanner$
   * @param finalBanner$
   * @private
   */
  private getProductBannerData$(
    defaultBanner$: Observable<Banner>,
    finalBanner$: Observable<Banner>
  ): Observable<ProductBanner> {
    return defaultBanner$.pipe(
      catchError(() => EMPTY),
      switchMap(defaultBanner =>
        defaultBanner ? concat(of(defaultBanner), this.getFinalProductBanner(finalBanner$, defaultBanner)) : EMPTY
      )
    );
  }

  private getFinalProductBanner(finalBanner$: Observable<Banner>, defaultBanner: Banner): Observable<Banner> {
    return this.journeyService.update$.pipe(
      filter(Boolean),
      take(1),
      map(() =>
        this.journeyService.guid
          ? finalBanner$.pipe(
              catchError(err => {
                this.handleInvalidJourneyIdError(err);
                return of(null);
              })
            )
          : of(null)
      ),
      switchMap(fBanner$ => (this.platform.isBrowser ? fBanner$ : EMPTY)),
      map(finalBanner => finalBanner || defaultBanner),
      map(data => data && { ...data, loaded: this.platform.isBrowser })
    );
  }

  /**
   * Preload banner data on server side, so it will be available
   * in Apollo Cache and forwarded to the client in TransferState.
   * @private
   */
  private preloadDefaultBannerData(): void {
    if (this.platform.isServer) {
      this.getDefaultMegaMenuProductBanner$().subscribe({ error: _ => {} });
      this.megaMenuMiniTeaserBanners$.subscribe();
    }
  }

  /**
   * Purge product banner data from Apollo Cache on submitting a user journey update,
   * so new data will be requested from endpoint afterward.
   * @private
   */
  private purgeCachedFinalProductBanner(): void {
    this.journeyService.update$.pipe(filter(update => !update)).subscribe(() => {
      const args = this.productBannerInputArgs;
      ['megaMenuProductBanner', 'gridPageProductBanner'].forEach(fieldName => {
        this.apolloCache.evict({
          id: 'ROOT_QUERY',
          fieldName,
          args
        });
      });
      this.apolloCache.gc();
    });
  }

  /**
   * Request mega-menu default banner.
   * On server side response will be cached in Redis.
   * @private
   */
  private getDefaultMegaMenuProductBanner$(): Observable<Banner> {
    return this.megaMenuProductBannerGql
      .fetch({ localization: this.helper.localization }, BANNER_CACHE_OPTIONS)
      .pipe(map(({ data }) => data.megaMenuProductBanner));
  }

  /**
   * Request final mega-menu banner.
   * Defer observable to apply latest journey guid.
   * @private
   */
  private getFinalMegaMenuProductBanner$(): Observable<Banner> {
    return defer(() => this.megaMenuProductBannerGql.fetch(this.productBannerInputArgs)).pipe(
      map(({ data }) => data.megaMenuProductBanner)
    );
  }

  /**
   * Request final grid-page banner.
   * Defer observable to apply latest journey guid.
   * @private
   */
  private getFinalGridPageProductBanner$(): Observable<Banner> {
    return defer(() => this.gridPageProductBannerGql.fetch(this.productBannerInputArgs)).pipe(
      map(({ data }) => data.gridPageProductBanner)
    );
  }

  /**
   * Request a marketing banner on client side only, after journey has been updated.
   * Defer observable for being able to apply latest journey guid and banned banners.
   * Response data won't be cached.
   * @private
   */
  private getMarketingBannerData$(): Observable<Banner> {
    return this.platform.isBrowser
      ? defer(() =>
          this.journeyService.update$.pipe(
            filter(Boolean),
            switchMap(() =>
              this.isMarketingBannerEnabled
                ? this.marketingBannerGql
                    .fetch(
                      {
                        filter: {
                          ...this.bannerFilterInput,
                          permanentlyBannedBannerIds: this.bannedMarketingBanners
                        },
                        localization: this.helper.localization
                      },
                      { fetchPolicy: FetchPolicy.NO_CACHE }
                    )
                    .pipe(
                      catchError(err => {
                        this.handleInvalidJourneyIdError(err);
                        return EMPTY;
                      })
                    )
                : EMPTY
            ),
            map(({ data }) => data.marketingBanner),
            filter(Boolean)
          )
        )
      : EMPTY;
  }

  /**
   * Request MTB for either mega menu or category pages.
   * For category pages, a category id has to be added,
   * whereas for mega menu the query should be cached.
   * @param isCategoryPage
   * @private
   */
  private getMiniTeaserBanners$(isCategoryPage = false): Observable<MiniTeaserBanner[]> {
    return defer(() =>
      this.miniTeaserBannersGql.fetch(
        {
          ...(isCategoryPage ? { categoryId: this.helper.categoryId } : null),
          localization: this.helper.localization
        },
        isCategoryPage ? null : BANNER_CACHE_OPTIONS
      )
    ).pipe(
      map(({ data }) => data.miniTeaserBanners),
      filter(banners => !!banners?.length),
      tap(banners => this.helper.trackMiniTeaserBannersImpression(banners)),
      catchError(() => EMPTY)
    );
  }

  private handleInvalidJourneyIdError(error: ApolloError): void {
    if (error.graphQLErrors?.some(e => e?.extensions.code === BannerErrorCode.INVALID_JOURNEY_ID)) {
      this.journeyService.removeGuid();
    }
  }
}
